import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { captureGeo, isAccurateFix, GPS_ACCURACY_WARN_M } from './_geo.js';
import { Modal } from '../../../../shared/components/Modal.js';
import { siteHistoryAtPath, type SiteHistory } from '../../../../shared/contracts/site-history.js';
import { LastWorkList } from '../../components/LastWorkAtLocation.js';
import {
  areaUnitOptions,
  dealerTerritoryId,
  listRows,
  type CompanyOption,
  type CustomerOption,
  type TerritoryOption,
  type WorkTypeOption,
} from '../../../../shared/contracts/job-request.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

// M06-03 (UI-Audit C5 / §19) — auto-save cadence. Configurable; default 60s.
const AUTO_SAVE_MS = 60_000;

type Step = 1 | 2 | 3 | 4;

// M06-02 (UI-Audit C3 / TR-03) — backend duplicate-site detection contract.
// A duplicate never refuses the job: the officer is warned, the approver decides.
interface DuplicateCheckResponse {
  result: 'NO_DUPLICATE' | 'POTENTIAL_DUPLICATE' | 'CONFIRMED_DUPLICATE';
  action: 'ALLOW' | 'WARN';
  /** The conflicting jobs by number, which is what the officer can look up (G18). */
  conflicts: { jobRequestId: string; jobNumber: string | null }[];
}

/** The wizard collects exactly the CreateJobRequestDto columns, nothing else. */
interface DraftState {
  companyId: string;
  customerId: string;
  customerName: string;
  territoryId: string;
  workTypeId: string;
  areaRequested: string;
  areaUnit: string;
  /** The surface as measured on site. Area is what these two produce. */
  height: string;
  width: string;
  latitude: string;
  longitude: string;
  locationAccuracy: string;
  remarks: string;
}

const EMPTY: DraftState = {
  companyId: '', customerId: '', customerName: '', territoryId: '', workTypeId: '',
  areaRequested: '', areaUnit: 'SQ_FT', height: '', width: '',
  latitude: '', longitude: '', locationAccuracy: '', remarks: '',
};

export function SalesJobNewPage(): ReactNode {
  const { at } = useBusinessDay();
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>(1);
  const [draft, setDraft] = useState<DraftState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoNote, setGeoNote] = useState<string | null>(null);
  /** Whether the chosen dealer carries a position of its own. */
  const [dealerPositioned, setDealerPositioned] = useState<boolean | null>(null);
  const [dupResult, setDupResult] = useState<DuplicateCheckResponse | null>(null);
  const [dupBusy, setDupBusy] = useState(false);
  /** R14 — earlier work at the chosen site, and the site the raiser accepted it for. */
  const [siteHistory, setSiteHistory] = useState<SiteHistory | null>(null);
  const [siteHistoryOpen, setSiteHistoryOpen] = useState(false);
  const [acceptedSite, setAcceptedSite] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [saveNote, setSaveNote] = useState<string | null>(null);

  const [companies, setCompanies] = useState<readonly CompanyOption[]>([]);
  const [customers, setCustomers] = useState<readonly CustomerOption[]>([]);
  const [territories, setTerritories] = useState<readonly TerritoryOption[]>([]);
  const [workTypes, setWorkTypes] = useState<readonly WorkTypeOption[]>([]);

  const draftRef = useRef<DraftState>(draft);
  const draftIdRef = useRef<string | null>(null);
  const savingRef = useRef(false);
  /** The dealer whose position is wanted; a reply for any other is stale. */
  const positionForRef = useRef<string | null>(null);

  const set = (k: keyof DraftState, v: string) => setDraft((d) => ({ ...d, [k]: v }));

  /**
   * A measurement, and the area it produces.
   *
   * Area used to be typed separately, which allowed the job to hold two
   * answers to the same question. It is now derived here so the form has one
   * source for it, and is left blank until both measurements are present
   * rather than showing a zero that was never measured.
   */
  const setMeasurement = (k: 'height' | 'width', v: string) =>
    setDraft((d) => {
      const next = { ...d, [k]: v };
      const h = Number(next.height);
      const w = Number(next.width);
      next.areaRequested = h > 0 && w > 0 ? String(h * w) : '';
      return next;
    });
  useEffect(() => { draftRef.current = draft; }, [draft]);

  // Each picker is independent: one that fails leaves its own list empty rather
  // than stranding the whole wizard — and says so, kept apart from `error`
  // because moving between steps clears that.
  useEffect(() => {
    const failed = (e: unknown) => setLookupError((e as Error).message);
    void apiRequest<unknown>('/companies', { token })
      .then((r) => setCompanies(listRows<CompanyOption>(r, 'items'))).catch((e) => { setCompanies([]); failed(e); });
    void apiRequest<unknown>('/master/customers?limit=200', { token })
      .then((r) => setCustomers(listRows<CustomerOption>(r, 'data'))).catch((e) => { setCustomers([]); failed(e); });
    void apiRequest<unknown>('/v1/territories?limit=200', { token })
      .then((r) => setTerritories(listRows<TerritoryOption>(r, 'data'))).catch((e) => { setTerritories([]); failed(e); });
    void apiRequest<unknown>('/master/work-types', { token })
      .then((r) => setWorkTypes(listRows<WorkTypeOption>(r, 'data'))).catch((e) => { setWorkTypes([]); failed(e); });
  }, [token]);

  // M06-03 (§19) — Save Draft: create on first save, update thereafter.
  const saveDraftNow = useCallback(async (): Promise<void> => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaveNote(t('sales.new.saving'));
    try {
      const cur = draftRef.current;
      const body = {
        companyId: cur.companyId || null,
        customerId: cur.customerId || null,
        customerName: cur.customerName || null,
        territoryId: cur.territoryId || null,
        workTypeId: cur.workTypeId || null,
        areaRequested: cur.areaRequested ? Number(cur.areaRequested) : null,
        areaUnit: cur.areaUnit || null,
        height: cur.height ? Number(cur.height) : null,
        width: cur.width ? Number(cur.width) : null,
        latitude: cur.latitude ? Number(cur.latitude) : null,
        longitude: cur.longitude ? Number(cur.longitude) : null,
        locationAccuracy: cur.locationAccuracy ? Number(cur.locationAccuracy) : null,
        remarks: cur.remarks || null,
        surveyData: { ...cur },
      };
      const id = draftIdRef.current;
      if (id) {
        await apiRequest(`/v1/job-requests/drafts/${id}`, { method: 'PUT', token, body });
      } else {
        const res = await apiRequest<{ draftId: string }>('/v1/job-requests/drafts', { method: 'POST', token, body });
        draftIdRef.current = res.draftId;
        setDraftId(res.draftId);
      }
      setSaveNote(t('sales.new.draftSaved', { time: at(new Date()) }));
    } catch {
      setSaveNote(t('sales.new.autosaveFailed'));
    } finally {
      savingRef.current = false;
    }
  }, [t, token]);

  useEffect(() => {
    const timer = setInterval(() => { void saveDraftNow(); }, AUTO_SAVE_MS);
    return () => clearInterval(timer);
  }, [saveDraftNow]);

  // Resume hydrates from the T084 columns, not from surveyData, so a draft saved
  // by the mobile app resumes here just as well.
  useEffect(() => {
    const resumeId = searchParams.get('draftId');
    if (!resumeId) return;
    void (async () => {
      try {
        const row = await apiRequest<Record<string, unknown>>(`/v1/job-requests/drafts/${resumeId}`, { token });
        const text = (k: string) => (row[k] == null ? '' : String(row[k]));
        setDraft({
          companyId: text('companyId'), customerId: text('customerId'), customerName: text('customerName'),
          territoryId: text('territoryId'), workTypeId: text('workTypeId'),
          areaRequested: text('areaRequested'), areaUnit: text('areaUnit') || 'SQ_FT',
          height: text('height'), width: text('width'),
          latitude: text('latitude'), longitude: text('longitude'),
          locationAccuracy: text('locationAccuracy'), remarks: text('remarks'),
        });
        draftIdRef.current = resumeId;
        setDraftId(resumeId);
        setSaveNote(t('sales.new.draftResumed'));
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, [searchParams, t, token]);

  const customer = useMemo(() => customers.find((c) => c.customerId === draft.customerId), [customers, draft.customerId]);
  const workType = useMemo(() => workTypes.find((w) => w.workTypeId === draft.workTypeId), [workTypes, draft.workTypeId]);

  function pickCustomer(id: string) {
    const row = customers.find((c) => c.customerId === id);
    // As on the phone: the dealer's own territory is taken as the answer, and
    // the officer can still change it.
    const territoryId = row ? dealerTerritoryId(row, territories) : null;
    // The site belongs to the dealer, so the previous dealer's position (and
    // the duplicate verdict about it) goes with him rather than surviving
    // until - or despite - the new dealer's position arriving.
    setDraft((d) => ({
      ...d,
      customerId: id,
      customerName: row?.companyName ?? d.customerName,
      territoryId: territoryId ?? d.territoryId,
      latitude: '',
      longitude: '',
      locationAccuracy: '',
    }));
    setDealerPositioned(null);
    setGeoNote(null);
    setDupResult(null);
    void inheritDealerPosition(id);
  }

  /**
   * The site is where the dealer is, not where the sales officer is standing.
   *
   * This step used to offer only "capture current location", which records the
   * device: an office, or 0,0 when the browser refused. Three of the six most
   * recent requests in RKH hold 0,0 - a point in the Atlantic that the painter
   * geofence then measured against.
   *
   * The dealer master is the source. Nothing is invented here: a dealer with no
   * position leaves the fields empty and capture remains available.
   */
  async function inheritDealerPosition(customerId: string): Promise<void> {
    positionForRef.current = customerId || null;
    if (!customerId) return;
    try {
      const full = await apiRequest<Record<string, unknown>>(
        `/master/customers/${encodeURIComponent(customerId)}`,
        { token },
      );
      if (positionForRef.current !== customerId) return;
      const lat = full.latitude;
      const lng = full.longitude;
      if (lat == null || lng == null) {
        setDealerPositioned(false);
        setGeoNote(t('sales.new.dealerHasNoPosition'));
        return;
      }
      setDealerPositioned(true);
      setDraft((d) => ({
        ...d,
        latitude: String(lat),
        longitude: String(lng),
        // Accuracy describes a device fix. This is a master record, so the
        // field stays empty rather than claiming a precision it has not got.
        locationAccuracy: '',
      }));
      setGeoNote(t('sales.new.positionFromDealer'));
    } catch {
      if (positionForRef.current !== customerId) return;
      setDealerPositioned(false);
      setGeoNote(t('sales.new.dealerPositionUnavailable'));
    }
  }

  async function grabLocation(): Promise<void> {
    setGeoBusy(true); setGeoNote(null);
    try {
      const fix = await captureGeo();
      setDraft((d) => ({
        ...d,
        latitude: String(fix.lat),
        longitude: String(fix.lng),
        locationAccuracy: fix.accuracyM == null ? '' : String(fix.accuracyM),
      }));
      const acc = fix.accuracyM == null ? '-' : `${fix.accuracyM}m`;
      setGeoNote(isAccurateFix(fix)
        ? t('sales.new.locCaptured', { acc })
        : t('sales.new.lowAccuracy', { acc, warn: String(GPS_ACCURACY_WARN_M) }));
    } catch (err) {
      setGeoNote((err as Error).message);
    } finally {
      setGeoBusy(false);
    }
  }

  function validateStep(s: Step): string | null {
    if (s === 1) {
      if (!draft.companyId) return t('sales.new.err.company');
      if (!draft.customerId) return t('sales.new.err.customer');
      if (!draft.customerName.trim()) return t('sales.new.err.customerName');
      if (!draft.territoryId) return t('sales.new.err.territory');
    }
    if (s === 2) {
      if (!draft.workTypeId) return t('sales.new.err.workType');
      if (!(Number(draft.height) > 0)) return t('sales.new.err.height');
      if (!(Number(draft.width) > 0)) return t('sales.new.err.width');
      if (!(Number(draft.areaRequested) > 0)) return t('sales.new.err.area');
      if (!draft.areaUnit) return t('sales.new.err.areaUnit');
    }
    if (s === 3) {
      if (draft.latitude === '' || draft.longitude === '') return t('sales.new.err.location');
      const lat = Number(draft.latitude);
      const lng = Number(draft.longitude);
      if (Number.isNaN(lat) || lat < -90 || lat > 90) return t('sales.new.err.latitude');
      if (Number.isNaN(lng) || lng < -180 || lng > 180) return t('sales.new.err.longitude');
    }
    return null;
  }

  // TR-03 — fails OPEN: a check error must never strand the officer here.
  async function runDuplicateCheck(): Promise<void> {
    setDupBusy(true);
    try {
      const res = await apiRequest<DuplicateCheckResponse>('/v1/job-requests/duplicate-check', {
        method: 'POST',
        token,
        body: {
          customerId: draft.customerId,
          workTypeId: draft.workTypeId,
          latitude: Number(draft.latitude),
          longitude: Number(draft.longitude),
        },
      });
      setDupResult(res);
    } catch {
      setDupResult(null);
    } finally {
      setDupBusy(false);
    }
  }

  /**
   * R14 — the painting already done, or in progress, at this site and for
   * this dealer. Fails open, as the duplicate check does: history that cannot
   * be read must not strand the officer.
   */
  async function loadSiteHistory(): Promise<SiteHistory | null> {
    try {
      const res = await apiRequest<SiteHistory>(
        siteHistoryAtPath(Number(draft.latitude), Number(draft.longitude), draft.customerId),
        { token },
      );
      const usable = res && Array.isArray(res.entries) ? res : null;
      setSiteHistory(usable);
      return usable;
    } catch {
      setSiteHistory(null);
      return null;
    }
  }

  /** The site and dealer a history answer — and an acceptance of it — belongs to. */
  const siteKey = `${Number(draft.latitude)},${Number(draft.longitude)},${draft.customerId}`;
  const priorWork = siteHistory?.entries ?? [];
  const priorWorkAccepted = priorWork.length > 0 && acceptedSite === siteKey;

  async function next() {
    const e = validateStep(step);
    if (e) { setError(e); return; }
    setError(null);
    if (step === 3) {
      await runDuplicateCheck();
      // The site is settled here, so this is where the raiser is shown the
      // painting already done or in progress there and for this dealer — and
      // must answer before he can go on.
      const history = await loadSiteHistory();
      if (history && history.entries.length > 0 && acceptedSite !== siteKey) {
        setSiteHistoryOpen(true);
        return;
      }
    }
    setStep((s) => (s < 4 ? ((s + 1) as Step) : s));
  }

  function acceptSiteHistory() {
    setAcceptedSite(siteKey);
    setSiteHistoryOpen(false);
    setStep(4);
  }

  function back() {
    setError(null);
    setStep((s) => (s > 1 ? ((s - 1) as Step) : s));
  }

  async function submit() {
    for (const s of [1, 2, 3] as Step[]) {
      const e = validateStep(s);
      if (e) { setError(e); setStep(s); return; }
    }
    setSubmitting(true); setError(null);
    try {
      const res = await apiRequest<{ jobRequestId: string }>('/v1/job-requests', {
        method: 'POST',
        token,
        body: {
          companyId: draft.companyId,
          customerId: draft.customerId,
          customerName: draft.customerName.trim(),
          territoryId: draft.territoryId,
          workTypeId: draft.workTypeId,
          areaRequested: Number(draft.areaRequested),
          areaUnit: draft.areaUnit,
          height: Number(draft.height),
          width: Number(draft.width),
          latitude: Number(draft.latitude),
          longitude: Number(draft.longitude),
          locationAccuracy: draft.locationAccuracy ? Number(draft.locationAccuracy) : null,
          remarks: draft.remarks.trim() || null,
          fromDraftId: draftIdRef.current,
          siteHistoryAcknowledged: priorWorkAccepted,
        },
      });
      navigate(`/sales/jobs/${res.jobRequestId}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const dupMessage = dupResult && dupResult.action === 'WARN'
    ? t(dupResult.result === 'CONFIRMED_DUPLICATE' ? 'sales.new.dupConfirmed' : 'sales.new.dupWarn', {
      jobs: dupResult.conflicts.map((c) => c.jobNumber ?? '—').join(', '),
    })
    : null;

  return (
    <main aria-label="sales-job-new">
      <h1>{t('sales.new.title')}</h1>
      <p aria-label="step-indicator">{t('sales.new.stepOf', { step: String(step) })}</p>
      <div>
        <button onClick={() => { void saveDraftNow(); }} aria-label="save-draft" type="button">{t('sales.new.saveDraft')}</button>
        {draftId ? <span aria-label="draft-id" hidden>{draftId}</span> : null}
        {saveNote ? <span role="status" aria-label="autosave-status">{saveNote}</span> : null}
      </div>
      {lookupError ? <p role="alert">{lookupError}</p> : null}
      {error ? <p role="alert">{error}</p> : null}

      {step === 1 && (
        <section aria-label="step-1-customer">
          <label>{t('sales.new.company')}<select aria-label="company" value={draft.companyId} onChange={(e) => set('companyId', e.target.value)}>
            <option value="">--</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
          </select></label>
          <label>{t('sales.new.customer')}<select aria-label="customer" value={draft.customerId} onChange={(e) => pickCustomer(e.target.value)}>
            <option value="">--</option>
            {customers.map((c) => <option key={c.customerId} value={c.customerId}>{c.dealerCode} — {c.companyName}</option>)}
          </select></label>
          <label>{t('sales.new.customerName')}<input aria-label="customer-name" value={draft.customerName} onChange={(e) => set('customerName', e.target.value)} /></label>
          <label>{t('sales.new.territory')}<select aria-label="territory" value={draft.territoryId} onChange={(e) => set('territoryId', e.target.value)}>
            <option value="">--</option>
            {territories.map((tr) => <option key={tr.territoryId} value={tr.territoryId}>{tr.territoryCode} — {tr.territoryName}</option>)}
          </select></label>
        </section>
      )}

      {step === 2 && (
        <section aria-label="step-2-work">
          <label>{t('sales.new.workType')}<select aria-label="work-type" value={draft.workTypeId} onChange={(e) => set('workTypeId', e.target.value)}>
            <option value="">--</option>
            {workTypes.map((w) => <option key={w.workTypeId} value={w.workTypeId}>{w.workTypeName}</option>)}
          </select></label>
          {/*
            The wall is measured, not the area guessed. Area is what the two
            measurements produce, so it is shown rather than typed — one
            figure, one source, and the branding request the job raises has
            the height and width it refuses to go without.
          */}
          <label>{t('sales.new.height')}<input aria-label="height" type="number" min={0} step="0.01" value={draft.height} onChange={(e) => setMeasurement('height', e.target.value)} /></label>
          <label>{t('sales.new.width')}<input aria-label="width" type="number" min={0} step="0.01" value={draft.width} onChange={(e) => setMeasurement('width', e.target.value)} /></label>
          <p aria-label="area-requested">{t('sales.new.areaIs', { area: String(Number(draft.areaRequested) || 0), unit: unitLabel(draft.areaUnit) })}</p>
          <label>{t('sales.new.areaUnit')}<select aria-label="area-unit" value={draft.areaUnit} onChange={(e) => set('areaUnit', e.target.value)}>
            {areaUnitOptions(draft.areaUnit).map((u) => <option key={u} value={u}>{unitLabel(u)}</option>)}
          </select></label>
          <label>{t('sales.new.remarks')}<input aria-label="remarks" value={draft.remarks} onChange={(e) => set('remarks', e.target.value)} /></label>
        </section>
      )}

      {step === 3 && (
        <section aria-label="step-3-location">
          {/*
            The site is the dealer's place, inherited from the master. Capture
            is offered only when the dealer has no position of its own —
            capturing otherwise records the officer's office, which is how
            three of RKH's six recent requests came to hold 0,0.
          */}
          <p aria-label="site-source">
            {dealerPositioned === true
              ? t('sales.new.siteFromDealer')
              : dealerPositioned === false
                ? t('sales.new.siteNeedsCapture')
                : t('sales.new.siteChooseDealer')}
          </p>
          {dealerPositioned !== true ? (
            <button type="button" aria-label="capture-gps" onClick={() => { void grabLocation(); }} disabled={geoBusy}>
              {geoBusy ? t('sales.new.capturing') : t('sales.new.captureLocation')}
            </button>
          ) : null}
          {geoNote ? <p role="status" aria-label="gps-note">{geoNote}</p> : null}
          <label>{t('sales.new.gpsLat')}<input aria-label="site-gps-lat" type="number" step="any" min={-90} max={90} value={draft.latitude} onChange={(e) => set('latitude', e.target.value)} /></label>
          <label>{t('sales.new.gpsLng')}<input aria-label="site-gps-lng" type="number" step="any" min={-180} max={180} value={draft.longitude} onChange={(e) => set('longitude', e.target.value)} /></label>
          {draft.locationAccuracy ? <span aria-label="gps-accuracy">{draft.locationAccuracy}m</span> : null}
          <button type="button" aria-label="check-duplicate" onClick={() => { void runDuplicateCheck(); }} disabled={dupBusy}>
            {dupBusy ? t('sales.new.checking') : t('sales.new.checkDuplicate')}
          </button>
          {dupMessage ? <p role="alert" aria-label="duplicate-warning">{dupMessage}</p> : null}
        </section>
      )}

      {step === 4 && (
        <section aria-label="step-4-review">
          <p>{t('sales.new.reviewHelp')}</p>
          <dl>
            <dt>{t('sales.new.customer')}</dt><dd aria-label="review-customer">{draft.customerName || customer?.companyName}</dd>
            <dt>{t('sales.new.territory')}</dt><dd aria-label="review-territory">{territories.find((tr) => tr.territoryId === draft.territoryId)?.territoryName ?? draft.territoryId}</dd>
            <dt>{t('sales.new.workType')}</dt><dd aria-label="review-work-type">{workType?.workTypeName ?? draft.workTypeId}</dd>
            <dt>{t('sales.new.measured')}</dt><dd aria-label="review-measurement">{Number(draft.height)} × {Number(draft.width)} {unitLabel(draft.areaUnit)}</dd>
            <dt>{t('sales.new.area')}</dt><dd aria-label="review-area">{Number(draft.areaRequested)} {unitLabel(draft.areaUnit)}</dd>
            <dt>{t('sales.new.location')}</dt><dd aria-label="review-location">{Number(draft.latitude)}, {Number(draft.longitude)}</dd>
            {priorWorkAccepted ? (
              <>
                <dt>{t('siteHistory.title')}</dt>
                <dd aria-label="review-site-history">{t('siteHistory.popup.noted', { count: String(priorWork.length) })}</dd>
              </>
            ) : null}
          </dl>
        </section>
      )}

      <Modal
        open={siteHistoryOpen}
        onClose={() => setSiteHistoryOpen(false)}
        title={t('siteHistory.popup.title')}
        size="lg"
        closeLabel={t('siteHistory.popup.back')}
        data-testid="site-history-popup"
        footer={(
          <>
            <button type="button" aria-label="site-history-back" onClick={() => setSiteHistoryOpen(false)}>
              {t('siteHistory.popup.back')}
            </button>
            <button type="button" aria-label="site-history-proceed" onClick={acceptSiteHistory}>
              {t('siteHistory.popup.proceed')}
            </button>
          </>
        )}
      >
        {siteHistory ? <LastWorkList history={siteHistory} /> : null}
      </Modal>

      <div>
        {step > 1 ? <button type="button" aria-label="step-back" onClick={back}>{t('sales.new.back')}</button> : null}
        {step < 4
          ? <button type="button" aria-label="step-next" onClick={() => { void next(); }} disabled={dupBusy}>{t('sales.new.next')}</button>
          : <button type="button" aria-label="step-submit" onClick={() => { void submit(); }} disabled={submitting}>{t('sales.new.submit')}</button>}
      </div>
    </main>
  );
}
